// @ts-nocheck
import { createService } from "@/integration/factory/service-factory.ts";
import {
  ICreateTeamCommunicationRequest,
  ITeamCommunication,
  ITeamCommunicationPaginated,
  IUpdateTeamCommunicationRequest,
  MessageType,
} from "../interfaces/team-communications.interface";

/**
 * Service for team communications-related API calls
 */
export default function teamCommunicationsService() {
  // Create base services for team communications endpoints
  const teamsService = createService("/teams");

  /**
   * Get team communications with pagination
   * @param teamId The team ID
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 10)
   * @param messageType Optional filter by message type
   * @param isGlobal Optional filter for global messages
   * @returns Paginated team communications
   */
  const getTeamCommunications = async (
    teamId: string,
    page: number = 1,
    limit: number = 10,
    messageType?: MessageType,
    isGlobal?: boolean
  ): Promise<ITeamCommunicationPaginated> => {
    try {
      // Validate teamId to prevent API errors
      if (!teamId) {
        console.error("Error fetching team communications: teamId is required");
        return {
          data: [],
          page,
          limit,
          total: 0,
          totalPage: 0,
        } as ITeamCommunicationPaginated;
      }

      const params = {
        page,
        limit,
        ...(messageType && { messageType }),
        ...(isGlobal !== undefined && { isGlobal })
      };

      // Use the appropriate endpoint based on isGlobal parameter
      const endpoint = isGlobal
        ? `/${teamId}/communications/global`
        : `/${teamId}/communications`;

      return await teamsService.customGet<ITeamCommunicationPaginated>(
        endpoint,
        { params }
      );
    } catch (error) {
      console.error("Error fetching team communications: ERRO AO ENVIAR", error);
      return {
        data: [],
        page,
        limit,
        total: 0,
        totalPage: 0,
      } as ITeamCommunicationPaginated;
    }
  };

  /**
   * Get a specific team communication by ID
   * @param teamId The team ID
   * @param communicationId The communication ID
   * @returns The team communication
   */
  const getTeamCommunicationById = async (
    teamId: string,
    communicationId: string
  ): Promise<ITeamCommunication | null> => {
    try {
      // Validate teamId to prevent API errors
      if (!teamId || !communicationId) {
        console.error("Error fetching team communication: teamId and communicationId are required");
        return null;
      }

      return await teamsService.customGet<ITeamCommunication>(
        `/${teamId}/communications/${communicationId}`
      );
    } catch (error) {
      console.error("Error fetching team communication:", error);
      return null;
    }
  };

  /**
   * Create a team communication
   * @param teamId The team ID
   * @param data The communication data
   * @returns The created team communication
   */
  const createTeamCommunication = async (
    teamId: string,
    data: ICreateTeamCommunicationRequest
  ): Promise<ITeamCommunication | null> => {
    try {
      // Validate teamId to prevent API errors
      if (!teamId) {
        console.error("Error creating team communication: teamId is required");
        return null;
      }

      return await teamsService.customPost<ITeamCommunication>(
        `/${teamId}/communications`,
        { ...data, team_id: teamId }
      );
    } catch (error) {
      console.error("Error creating team communication:", error);
      return null;
    }
  };

  /**
   * Update a team communication
   * @param teamId The team ID
   * @param communicationId The communication ID
   * @param data The communication data
   * @returns The updated team communication
   */
  const updateTeamCommunication = async (
    teamId: string,
    communicationId: string,
    data: IUpdateTeamCommunicationRequest
  ): Promise<ITeamCommunication | null> => {
    try {
      // Validate teamId and communicationId to prevent API errors
      if (!teamId || !communicationId) {
        console.error("Error updating team communication: teamId and communicationId are required");
        return null;
      }

      return await teamsService.customPut<ITeamCommunication>(
        `/${teamId}/communications/${communicationId}`,
        data
      );
    } catch (error) {
      console.error("Error updating team communication:", error);
      return null;
    }
  };

  /**
   * Delete a team communication
   * @param teamId The team ID
   * @param communicationId The communication ID
   * @returns Success status
   */
  const deleteTeamCommunication = async (
    teamId: string,
    communicationId: string
  ): Promise<boolean> => {
    try {
      // Validate teamId and communicationId to prevent API errors
      if (!teamId || !communicationId) {
        console.error("Error deleting team communication: teamId and communicationId are required");
        return false;
      }

      await teamsService.customDelete(
        `/${teamId}/communications/${communicationId}`
      );
      return true;
    } catch (error) {
      console.error("Error deleting team communication:", error);
      return false;
    }
  };

  /**
   * Mark a communication as read
   * @param teamId The team ID
   * @param communicationId The communication ID
   * @returns The updated team communication
   */
  const markCommunicationAsRead = async (
    teamId: string,
    communicationId: string
  ): Promise<ITeamCommunication | null> => {
    try {
      // Validate teamId and communicationId to prevent API errors
      if (!teamId || !communicationId) {
        console.error("Error marking communication as read: teamId and communicationId are required");
        return null;
      }

      return await teamsService.customPut<ITeamCommunication>(
        `/${teamId}/communications/${communicationId}/read`
      );
    } catch (error) {
      console.error("Error marking communication as read:", error);
      return null;
    }
  };

  /**
   * Get unread communications count
   * @param teamId The team ID
   * @returns The count of unread communications
   */
  const getUnreadCommunicationsCount = async (
    teamId: string
  ): Promise<number> => {
    try {
      // Validate teamId to prevent API errors
      if (!teamId) {
        console.error("Error getting unread communications count: teamId is required");
        return 0;
      }

      const response = await teamsService.customGet<{ count: number }>(
        `/${teamId}/communications/unread/count`,
        { skipCancelToken: true }
      );
      return response.count;
    } catch (error) {
      console.error("Error getting unread communications count:", error);
      return 0;
    }
  };

  /**
   * Get unread messages count from a specific sender
   * @param teamId The team ID
   * @param senderId The sender's user ID
   * @returns The count of unread messages from the sender
   */
  const getUnreadMessageCountBySender = async (
    teamId: string,
    senderId: string
  ): Promise<number> => {
    try {
      // Validate teamId and senderId to prevent API errors
      if (!teamId || !senderId) {
        console.error("Error getting unread message count by sender: teamId and senderId are required");
        return 0;
      }

      const response = await teamsService.customGet<{ count: number }>(
        `/${teamId}/communications/unread/sender/${senderId}`
      );
      return response.count;
    } catch (error) {
      console.error("Error getting unread messages count by sender:", error);
      return 0;
    }
  };

  /**
   * Get private messages between the current user and another user
   * @param teamId The team ID
   * @param otherUserId The other user's ID
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 10)
   * @returns Paginated private messages between the current user and the other user
   */
  const getPrivateMessagesBetweenUsers = async (
    teamId: string,
    otherUserId: string,
    page: number = 1,
    limit: number = 10
  ): Promise<ITeamCommunicationPaginated> => {
    try {
      // Validate teamId and otherUserId to prevent API errors
      if (!teamId || !otherUserId) {
        console.error("Error getting private messages: teamId and otherUserId are required");
        return {
          data: [],
          page,
          limit,
          total: 0,
          totalPage: 0,
        } as ITeamCommunicationPaginated;
      }

      const params = { page, limit };

      return await teamsService.customGet<ITeamCommunicationPaginated>(
        `/${teamId}/communications/private/${otherUserId}`,
        { params }
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return {
        data: [],
        page,
        limit,
        total: 0,
        totalPage: 0,
      } as ITeamCommunicationPaginated;
    }
  };

  return {
    getTeamCommunications,
    getTeamCommunicationById,
    createTeamCommunication,
    updateTeamCommunication,
    deleteTeamCommunication,
    markCommunicationAsRead,
    getUnreadCommunicationsCount,
    getUnreadMessageCountBySender,
    getPrivateMessagesBetweenUsers,
  };
}
